import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { useEffect, useState } from "react";
import { useController } from "react-hook-form";
import { dynamicAPICall } from "../../../utils/helperFns";
import customFunctions from "../CustomFunctions/customFunctions";
import ErrorMessageHandle from "../Error/ErrorMessageHandle";

interface DropdownProps {
  options?: any[];
  state: any;
  setState: any;
  error?: any;
  register?: any;
  field: any;
  clearErrors?: any;
  callbackEvents?: any;
  control: any;
  isDisabled?: boolean;
}

export const Dropdown = ({
  options,
  state,
  setState,
  error,
  field,
  clearErrors,
  callbackEvents,
  control,
  isDisabled = false,
}: DropdownProps) => {
  const [optionsList, setOptionsListRaw] = useState<any[]>(Array.isArray(options) ? options : []);
  const setOptionsList = (values: any) => setOptionsListRaw(Array.isArray(values) ? values : []);
  const [defaultValue, setDefaultValue] = useState("");

  useEffect(() => {
    // Extract onload event and query
    const onloadGraphqlEvent = field?.events?.find(
      (x) => x.event === "onload" && x.type === "graphql",
    );
    const onLoadCustomFunction = field?.events?.find(
      (x) => x.event === "onload" && x.type === "customfunction",
    );
    if (onloadGraphqlEvent) {
      const { query = "", responseVariable = "" } = onloadGraphqlEvent?.query || {};
      if (query && responseVariable) {
        dynamicAPICall(query, responseVariable).then((result) => {
          setOptionsList(result || []);
        });
      }
    } else if (onLoadCustomFunction) {
      // Custom functions may be sync (static lists) or async (API-backed lists).
      Promise.resolve(customFunctions[onLoadCustomFunction?.function_name]?.()).then((values) =>
        setOptionsList(values || []),
      );
    }
  }, [field?.events]);

  // Option values can arrive as numbers (e.g. ids from an API) while the stored value is a
  // string, or vice versa - compare as strings so the label still resolves.
  const isSameValue = (a: any, b: any) =>
    a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b);

  useEffect(() => {
    if (optionsList?.length > 0) {
      const stateVal = state?.[field.name];
      let defValue =
        optionsList.find(
          (x) =>
            isSameValue(x?.value || x?.label, stateVal?.[0]?.value) ||
            isSameValue(x?.value || x?.label, stateVal?.[0]?.label) ||
            isSameValue(x?.value || x?.label, stateVal),
        ) || null;
      if (defValue) {
        setDefaultValue(defValue?.value || defValue?.label);
      } else {
        defValue =
          optionsList.find(
            (x) =>
              isSameValue(x?.value || x?.label, field?.default_value?.[0]) ||
              isSameValue(x?.value || x?.label, field?.default_value),
          ) || null;
        if (defValue) {
          setDefaultValue(defValue?.value || defValue?.label);
        }
      }
    }
  }, [state, field.name, optionsList, field?.default_value]);

  const validateDropdown = (value: any) => {
    const required = field?.validations?.find((x) => x.type === "required");
    if (required && (!value || value === "")) {
      return required?.message || "This field is required";
    }
    return true;
  };

  const { field: controllerField } = useController({
    name: field?.name,
    control,
    defaultValue,
    rules: { validate: validateDropdown },
  });
  const { value: formValue, onChange: setFormValue } = controllerField;

  // The saved value can sit nested under a parent field (e.g. a radio's dependent field), where
  // the form has no value of its own for this name - so it starts empty even though the select
  // is showing the saved choice. Keep the two the same, or Submit reports a filled-in dropdown
  // as required.
  useEffect(() => {
    if (defaultValue && !formValue) {
      setFormValue(defaultValue);
    }
  }, [defaultValue, formValue, setFormValue]);

  const handleChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value;

    // Clear errors
    if (clearErrors) {
      clearErrors(field?.name);
    }

    // Update state
    setState({ ...state, [field?.name]: newValue });

    // Call React Hook Form's onChange
    setFormValue(newValue);

    // Handle custom events
    if (field?.events && callbackEvents) {
      callbackEvents(event, field?.events);
    }
  };

  return (
    <Box id={`${field?.name}_id`}>
      <FormControl fullWidth error={!!error}>
        <Select
          {...controllerField}
          labelId={`${field?.name}-label`}
          id={`${field?.name}-select`}
          value={formValue || defaultValue}
          variant='filled'
          disabled={isDisabled}
          error={!!error}
          onChange={handleChange}
          displayEmpty
          renderValue={(selected) => {
            if (!selected || selected === "") {
              return (
                <span style={{ color: "#999" }}>{field?.placeholder || "Select an option"}</span>
              );
            }
            const matchedOption = optionsList?.find((x) =>
              isSameValue(x?.value || x?.label, selected),
            );
            return matchedOption?.label || selected;
          }}>
          {optionsList?.map((item, index) => {
            return (
              <MenuItem
                key={index}
                value={item?.value || item?.label}
                disabled={item?.disabled ? item?.disabled || false : false}>
                {item?.label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <ErrorMessageHandle error={error} />
    </Box>
  );
};

Dropdown.defaultProps = {
  options: [],
  error: undefined,
  register: () => {},
  clearErrors: () => {},
  callbackEvents: () => {},
};
